import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth';
import { createUserCloudFunction, auth } from '@/firebase/init';
import { getFirebaseAuthErrorMessage } from './firebaseAuthErrors';
import { googleAuthProvider } from './googleProvider';
import { loginSchema, registerSchema } from './firebaseAuthSchemas';
import type { LoginFormValues, RegisterFormValues } from './firebaseAuthSchemas';
import type { AuthMode, ResetMessageState } from './firebaseAuthTypes';

function assignAfterAuth(redirectPath: string) {
  window.location.assign(redirectPath);
}

export function useFirebaseAuthCard(redirectPath: string) {
  const [mode, setMode] = useState<AuthMode>('login');
  const [authError, setAuthError] = useState<string | null>(null);
  const [formDisabled, setFormDisabled] = useState(false);
  const [resetMessage, setResetMessage] = useState<ResetMessageState>('idle');
  const [showPassword, setShowPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const registerForm = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      displayName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  const tabHint = useMemo(
    () =>
      mode === 'login' ? 'Log in to your account' : 'Create an account to get started',
    [mode],
  );

  const switchToLogin = () => {
    setMode('login');
    setAuthError(null);
    setResetMessage('idle');
  };

  const switchToRegister = () => {
    setMode('register');
    setAuthError(null);
    setResetMessage('idle');
  };

  const onLogin = async (data: LoginFormValues) => {
    setAuthError(null);
    setFormDisabled(true);
    try {
      await signInWithEmailAndPassword(auth, data.email, data.password);
      assignAfterAuth(redirectPath);
    } catch (e) {
      setAuthError(getFirebaseAuthErrorMessage(e, 'login'));
    } finally {
      setFormDisabled(false);
    }
  };

  const onRegister = async (data: RegisterFormValues) => {
    setAuthError(null);
    setFormDisabled(true);
    try {
      await createUserWithEmailAndPassword(auth, data.email, data.password);
      await createUserCloudFunction({ displayName: data.displayName });
      assignAfterAuth(redirectPath);
    } catch (e) {
      setAuthError(getFirebaseAuthErrorMessage(e, 'register'));
    } finally {
      setFormDisabled(false);
    }
  };

  const onGoogle = async () => {
    setAuthError(null);
    setFormDisabled(true);
    try {
      const result = await signInWithPopup(auth, googleAuthProvider);
      if (getAdditionalUserInfo(result)?.isNewUser) {
        await createUserCloudFunction({
          displayName: result.user.displayName ?? undefined,
        });
      }
      assignAfterAuth(redirectPath);
    } catch (e) {
      setAuthError(getFirebaseAuthErrorMessage(e, 'google'));
    } finally {
      setFormDisabled(false);
    }
  };

  const onForgotPassword = async () => {
    const email = loginForm.getValues('email').trim();
    if (!email) {
      setAuthError('Enter your email above, then click “Forgot password?”.');
      return;
    }
    if (!z.string().email().safeParse(email).success) {
      setAuthError('Enter a valid email address to receive a reset link.');
      return;
    }
    setAuthError(null);
    setResetMessage('sending');
    try {
      await sendPasswordResetEmail(auth, email);
      setResetMessage('sent');
    } catch (e) {
      setResetMessage('idle');
      setAuthError(getFirebaseAuthErrorMessage(e, 'passwordReset'));
    }
  };

  return {
    mode,
    authError,
    formDisabled,
    resetMessage,
    showPassword,
    setShowPassword,
    showRegisterPassword,
    setShowRegisterPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    loginForm,
    registerForm,
    tabHint,
    onLogin,
    onRegister,
    onGoogle,
    onForgotPassword,
    switchToLogin,
    switchToRegister,
  };
}
